
import { getCurrentConference } from '../base/conference';
import MeetHourJS, { MHRecordingConstants } from '../base/lib-meet-hour';
import { isLocalParticipantModerator } from '../base/participants';
import {
    NOTIFICATION_TIMEOUT,
    hideNotification,
    showErrorNotification,
    showNotification
} from '../notifications';

import {
    CLEAR_RECORDING_SESSIONS,
    LIVESTREAM_OBJECT,
    RECORDING_MODE,
    RECORDING_SESSION_UPDATED,
    RECORDING_STOP_REQUESTED,
    SET_PENDING_RECORDING_NOTIFICATION_UID,
    SET_STREAM_KEY,
    START_LOCAL_RECORDING,
    STOP_LOCAL_RECORDING
} from './actionTypes';
import { getActiveSession } from './functions';

/**
 * Clears the data of every recording sessions.
 *
 * @returns {{
 *     type: CLEAR_RECORDING_SESSIONS
 * }}
 */
export function clearRecordingSessions() {
    return {
        type: CLEAR_RECORDING_SESSIONS
    };
}

/**
 * Signals that the pending recording notification should be removed from the
 * screen.
 *
 * @param {string} streamType - The type of the stream ({@code 'file'} or
 * {@code 'stream'}).
 * @returns {Function}
 */
export function hidePendingRecordingNotification(streamType: string) {
    return (dispatch: Function, getState: Function) => {
        const { pendingNotificationUids } = getState()['features/recording'];
        const pendingNotificationUid = pendingNotificationUids[streamType];

        if (pendingNotificationUid) {
            dispatch(hideNotification(pendingNotificationUid));
            dispatch(
                _setPendingRecordingNotificationUid(
                    undefined, streamType));
        }
    };
}

/**
 * Starts local recording.
 *
 * @param {boolean} onlySelf - Whether to only record the local streams.
 * @returns {Object}
 */
export function startLocalVideoRecording(onlySelf: boolean) {
    return {
        type: START_LOCAL_RECORDING,
        onlySelf
    };
}

/**
 * Stops local recording.
 *
 * @returns {Object}
 */
export function stopLocalVideoRecording() {
    return {
        type: STOP_LOCAL_RECORDING
    };
}


/**
 * Sets the stream key last used by the user for later reuse.
 *
 * @param {string} streamKey - The stream key to set.
 * @returns {{
 *     type: SET_STREAM_KEY,
 *     streamKey: string
 * }}
 */
export function setLiveStreamKey(streamKey: string) {
    return {
        type: SET_STREAM_KEY,
        streamKey
    };
}

/**
 * Signals that the pending recording notification should be shown on the
 * screen.
 *
 * @param {string} streamType - The type of the stream ({@code file} or
 * {@code stream}).
 * @returns {Function}
 */
export function showPendingRecordingNotification(streamType: string) {
    return async (dispatch: Function, getState: Function) => {
        const isLiveStreaming
            = streamType === MeetHourJS.constants.recording.mode.STREAM;

        const isModerator = isLocalParticipantModerator(getState());

        const moderatorActions = isModerator
            ? {
                customActionNameKey: isLiveStreaming
                    ? 'dialog.stopLiveStreaming'
                    : 'dialog.stopRecording',
                customActionHandler: () => {
                    // Re-fetch state at click-time to avoid stale closure data.
                    // The session or conference reference may have changed since
                    // the notification was first shown.
                    const currentState = getState();
                    const currentConference = getCurrentConference(currentState);
                    const activeSession = getActiveSession(currentState, streamType);

                    if (activeSession) {
                        // Mark the session as "stopping" BEFORE calling stopRecording().
                        // This prevents the middleware from treating any incoming XMPP
                        // PENDING or ON events (from Jibri still transitioning) as a
                        // new recording start — which was causing the start sound and
                        // "recording started" notification to fire after a stop click.
                        // The server's real OFF event (via RECORDER_STATE_CHANGED) will
                        // play the stop sound and show the notification for everyone.
                        dispatch({
                            type: RECORDING_STOP_REQUESTED,
                            sessionId: activeSession.id
                        });

                        if (currentConference) {
                            currentConference.stopRecording(activeSession.id);
                            currentConference.sendCommand('recording-stop-requested', {
                                value: activeSession.id
                            });
                        }

                        // Clean up the pending notification banner immediately.
                        dispatch(hidePendingRecordingNotification(streamType));

                        return true;
                    }

                    return false;
                }
            }
            : {};

        const dialogProps = isLiveStreaming
            ? {
                descriptionKey: 'liveStreaming.pending',
                titleKey: 'dialog.liveStreaming',
                ...moderatorActions
            }
            : {
                descriptionKey: 'recording.pending',
                titleKey: 'dialog.recording',
                ...moderatorActions
            };

        const notification = await dispatch(
            showNotification({
                isDismissAllowed: false,
                ...dialogProps
            })
        );

        if (notification) {
            dispatch(
                _setPendingRecordingNotificationUid(
                    notification.uid,
                    streamType
                )
            );
        }
    };
}

/**
 * Signals that the recording error notification should be shown.
 *
 * @param {Object} props - The Props needed to render the notification.
 * @param {NOTIFICATION_TIMEOUT_TYPE} timeoutType - The type of timeout for the notification.
 * @returns {showErrorNotification}
 */
export function showRecordingError(props: Object, timeoutType) {
    return showErrorNotification(props, timeoutType);
}

/**
 * Signals that the stopped recording notification should be shown on the
 * screen for a given period.
 *
 * @param {string} streamType - The type of the stream ({@code file} or
 * {@code stream}).
 * @param {string?} participantName - The participant name stopping the recording.
 * @returns {showNotification}
 */
export function showStoppedRecordingNotification(streamType: string, participantName?: string) {
    const isLiveStreaming
        = streamType === MeetHourJS.constants.recording.mode.STREAM;
    const descriptionArguments = { name: participantName };
    const dialogProps = isLiveStreaming ? {
        descriptionKey: participantName ? 'liveStreaming.offBy' : 'liveStreaming.off',
        descriptionArguments,
        titleKey: 'dialog.liveStreaming'
    } : {
        descriptionKey: participantName ? 'recording.offBy' : 'recording.off',
        descriptionArguments,
        titleKey: 'dialog.recording'
    };

    return showNotification(dialogProps, NOTIFICATION_TIMEOUT);
}

/**
 * Signals that a started recording notification should be shown on the
 * screen for a given period.
 *
 * @param {string} streamType - The type of the stream ({@code file} or
 * {@code stream}).
 * @param {string} participantName - The participant name that started the recording.
 * @returns {showNotification}
 */
export function showStartedRecordingNotification(streamType: string, participantName: string) {
    const isLiveStreaming
        = streamType === MeetHourJS.constants.recording.mode.STREAM;
    const descriptionArguments = { name: participantName };
    const dialogProps = isLiveStreaming ? {
        descriptionKey: participantName ? 'liveStreaming.onBy' : 'liveStreaming.on',
        descriptionArguments,
        titleKey: 'dialog.liveStreaming'
    } : {
        descriptionKey: participantName ? 'recording.onBy' : 'recording.on',
        descriptionArguments,
        titleKey: 'dialog.recording'
    };

    return showNotification(dialogProps, NOTIFICATION_TIMEOUT);
}

/**
 * Updates the known state for a given recording session.
 *
 * @param {Object} session - The new state to merge with the existing state in
 * redux.
 * @returns {{
 *     type: RECORDING_SESSION_UPDATED,
 *     sessionData: Object
 * }}
 */
export function updateRecordingSessionData(session: Object) {
    const status = session.getStatus();
    const timestamp
        = status === MHRecordingConstants.status.ON
            ? Date.now() / 1000
            : undefined;

    return {
        type: RECORDING_SESSION_UPDATED,
        sessionData: {
            error: session.getError(),
            id: session.getID(),
            initiator: session.getInitiator(),
            liveStreamViewURL: session.getLiveStreamViewURL(),
            mode: session.getMode(),
            status,
            terminator: session.getTerminator(),
            timestamp,
            subSessions: session.getSubSessions()
        }
    };
}

/**
 * Sets UID of the the pending streaming notification to use it when hinding
 * the notification is necessary, or unsets it when undefined (or no param) is
 * passed.
 *
 * @param {?number} uid - The UID of the notification.
 * @param {string} streamType - The type of the stream ({@code file} or
 * {@code stream}).
 * @returns {{
 *     type: SET_PENDING_RECORDING_NOTIFICATION_UID,
 *     streamType: string,
 *     uid: number
 * }}
 */
function _setPendingRecordingNotificationUid(uid: ?number, streamType: string) {
    return {
        type: SET_PENDING_RECORDING_NOTIFICATION_UID,
        streamType,
        uid
    };
}

/**
 * Sets the recording mode.
 *
 * @param {string} mode - The recording mode.
 * @returns {{
 *     type: RECORDING_MODE,
 *     mode: string
 * }}
 */
export function setRecordingMode(mode: string) {

    return {
        type: RECORDING_MODE,
        mode
    };
}

/**
 * Sets the live stream object.
 *
 * @param {string} data - The live straobject.
 * @returns {{
 *     type: RECORDING_MODE,
 *     mode: string
 * }}
 */
export function setLiveStreamObject(data: Object) {
    return {
        type: LIVESTREAM_OBJECT,
        data
    };
}

